import { useState } from "react";
import { useLocalization } from "@fluent/react";
import { Button, Chip, IconButton, Popover } from "@narmi/design_system";
import { useCurrentUser } from "../../contexts/CurrentUserContext";
import SearchInput from "./SearchInput";
import { useRecentSearchesStorage } from "./storage";
import styles from "./RecentTransactionsModule.module.scss";

const MAX_RECENT_SEARCHES = 5;

interface DescriptionFilterChipProps {
  appliedSearch: string;
  onApply: (search: string) => void;
}

const DescriptionFilterChip = ({
  appliedSearch,
  onApply
}: DescriptionFilterChipProps) => {
  const { l10n } = useLocalization();
  const [isOpen, setIsOpen] = useState(false);
  const [currentSearch, setCurrentSearch] = useState(appliedSearch);
  const { currentUser } = useCurrentUser();
  const userId = currentUser?.id?.toString() || "";
  const [{ recentSearches }, setRecentSearchesState] =
    useRecentSearchesStorage(userId);

  const handleApply = () => {
    const trimmed = currentSearch.trim();
    onApply(trimmed);
    if (trimmed) {
      setRecentSearchesState((prev) => ({
        ...prev,
        recentSearches: [
          trimmed,
          ...prev.recentSearches.filter((s) => s !== trimmed)
        ].slice(0, MAX_RECENT_SEARCHES)
      }));
    }
    setIsOpen(false);
  };

  const handleClear = () => {
    setCurrentSearch("");
  };

  const handleRemoveRecentSearch = (search: string) => {
    setRecentSearchesState((prev) => ({
      ...prev,
      recentSearches: prev.recentSearches.filter((s) => s !== search)
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleApply();
  };

  return (
    <Popover
      isOpen={isOpen}
      onUserDismiss={() => setIsOpen(false)}
      content={
        <div className={styles.searchPopover}>
          <div className={styles.searchInputRow}>
            <SearchInput
              value={currentSearch}
              onChange={setCurrentSearch}
              onKeyDown={handleKeyDown}
              placeholder={l10n.getString(
                "recent-transactions-search-placeholder"
              )}
              autoFocus={isOpen}
            />
          </div>
          {recentSearches.length > 0 && (
            <>
              <div className={styles.recentSearchHeader}>
                {l10n.getString("recent-transactions-search-recent")}
              </div>
              <hr className={styles.accountListDividerNoMargin} />
              <ul className={styles.recentSearchList}>
                {recentSearches.map((s) => (
                  <li key={s} className={styles.recentSearchRow}>
                    <button
                      type="button"
                      className={`${styles.recentSearchItem} button--reset`}
                      onClick={() => {
                        setCurrentSearch(s);
                      }}
                    >
                      {s}
                    </button>
                    <IconButton
                      kind="plain"
                      name="x"
                      textSize="s"
                      label={l10n.getString(
                        "recent-transactions-search-remove-recent",
                        { search: s }
                      )}
                      onClick={() => handleRemoveRecentSearch(s)}
                    />
                  </li>
                ))}
              </ul>
            </>
          )}
          <hr className={styles.accountListDividerNoMargin} />
          <div className={styles.searchFooter}>
            <Button kind="plain" size="xs" onClick={handleClear}>
              {l10n.getString("recent-transactions-search-clear")}
            </Button>
            <Button size="xs" onClick={handleApply}>
              {l10n.getString("recent-transactions-search-apply")}
            </Button>
          </div>
        </div>
      }
    >
      <Chip
        label={l10n.getString("recent-transactions-filter-description")}
        kind={appliedSearch ? "primary" : "secondary"}
        endIcon={isOpen ? "chevron-up" : "chevron-down"}
        onClick={() => {
          setIsOpen((prev) => {
            if (!prev) {
              setCurrentSearch(appliedSearch);
            }
            return !prev;
          });
        }}
      />
    </Popover>
  );
};

export default DescriptionFilterChip;
